import { useEffect, useState } from "react";
import {
  DEFAULT_PUBLIC_FEATURES,
  getPublicFeatures,
  type PublicFeatureFlag,
  type PublicFeatures,
} from "@/services/api/features";

/**
 * Public feature flags for the signed-in customer.
 *
 * Flags are fetched once per page load and shared between all consumers.
 * Until the request resolves every flag reads as OFF, so gated UI stays hidden
 * instead of flashing in and out. A failed request keeps the OFF defaults.
 */

interface FeatureFlagsState {
  features: PublicFeatures;
  loaded: boolean;
}

let cachedState: FeatureFlagsState = { features: DEFAULT_PUBLIC_FEATURES, loaded: false };
let inflight: Promise<FeatureFlagsState> | null = null;
const listeners = new Set<(state: FeatureFlagsState) => void>();

function publish(state: FeatureFlagsState): void {
  cachedState = state;
  listeners.forEach((listener) => listener(state));
}

function loadFeatureFlags(): Promise<FeatureFlagsState> {
  if (cachedState.loaded) return Promise.resolve(cachedState);
  if (inflight) return inflight;

  inflight = getPublicFeatures()
    .then((features) => ({ features, loaded: true }))
    .catch(() => ({ features: DEFAULT_PUBLIC_FEATURES, loaded: true }))
    .then((state) => {
      publish(state);
      inflight = null;
      return state;
    });

  return inflight;
}

/** Test/support helper: forget the cached flags so the next hook mount refetches. */
export function resetFeatureFlagsCache(): void {
  cachedState = { features: DEFAULT_PUBLIC_FEATURES, loaded: false };
  inflight = null;
}

export interface UseFeatureFlagsResult {
  features: PublicFeatures;
  /** True once the flags have been fetched (successfully or not). */
  loaded: boolean;
  /** Convenience accessor: false while loading, then the backend value. */
  isEnabled: (flag: PublicFeatureFlag) => boolean;
}

export function useFeatureFlags(): UseFeatureFlagsResult {
  const [state, setState] = useState<FeatureFlagsState>(cachedState);

  useEffect(() => {
    listeners.add(setState);
    if (cachedState.loaded) {
      setState(cachedState);
    } else {
      void loadFeatureFlags();
    }
    return () => {
      listeners.delete(setState);
    };
  }, []);

  return {
    features: state.features,
    loaded: state.loaded,
    isEnabled: (flag) => state.loaded && state.features[flag] === true,
  };
}
